import * as React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom-v5-compat';
import queryString from 'query-string';

import History from './history';
import Recommendations from './recommendations';
import IndividualStudent from './individual_student'

import { SequencePracticeInterface, StudentHistoryInterface, } from '../interfaces'
import { DIAGNOSTIC_INSIGHTS_USE_ELL, DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE, NO_CLASSROOM, NO_STUDENT_ID, studentHistoryHasPreDiagnostic, timeframeOptions, forwardSlash, } from '../shared'
import { requestGet, } from '../../../../../../modules/request'
import {
  Checkbox,
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  SegmentedControl,
  Spinner,
  MobileSidebarButtonContainer,
} from '../../../../../Shared/index';

const fileDocumentImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`;
const chevronLeftImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/chevron-left.svg`;
const chevronRightImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/chevron-right.svg`;

const HISTORY = 'History';
const RECOMMENDATIONS = 'Recommendations';

const HistoryAndRecommendations = ({ teacherClassroomIds, classroom, openMobileSidebar, selectedStudentId, setSelectedStudentId, }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const timeframeFromLocalStorage = timeframeOptions.find(timeframe => timeframe.value === window.localStorage.getItem(DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE))

  const [useELLDiagnostics, setUseELLDiagnostics] = React.useState<boolean>(window.localStorage.getItem(DIAGNOSTIC_INSIGHTS_USE_ELL) === 'true');
  const [selectedTimeframe, setSelectedTimeframe] = React.useState(timeframeFromLocalStorage || timeframeOptions[0]);
  const [studentHistories, setStudentHistories] = React.useState<StudentHistoryInterface[]>(null)
  const [sequencePractice, setSequencePractice] = React.useState<SequencePracticeInterface>({})
  const [previouslyAssignedDiagnosticsByLevel, setPreviouslyAssignedDiagnosticsByLevel] = React.useState<DiagnosticsAssignedInterface>(null)

  const byClass = location.pathname.includes('by_class');

  const classroomIdRef = React.useRef(classroom?.id);

  React.useEffect(() => {
    classroomIdRef.current = classroom?.id;

    if (classroom?.id) {
      setPreviouslyAssignedDiagnosticsByLevel(null)
      getPreviouslyAssignedDiagnosticDataForClassroom()
    }
  }, [classroom?.id]);

  // Redirect if `by_class` but no `history` or `recommendations` at the end of the URL
  React.useEffect(() => {
    if (byClass && !/\/(history|recommendations)$/.test(location.pathname)) {
      navigate(`${location.pathname.replace(/\/$/, '')}/history`, { replace: true });
    }
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    if (byClass || selectedStudentId || !studentHistories) { return }

    const match = location.pathname.match(/by_student\/(\d+)$/);
    const studentId = match ? Number(match[1]) : null;

    if (studentId && studentHistories.find(s => s.user_id === studentId)) {
      setSelectedStudentId(studentId)
    } else if (studentHistories.length) {
      setSelectedStudentId(studentHistories[0].user_id)
    } else {
      setSelectedStudentId(NO_STUDENT_ID)
    }
  }, [location.pathname, selectedStudentId, studentHistories])

  React.useEffect(() => {
    if (byClass || !selectedStudentId) { return }

    navigate(`/teachers/progress_reports/diagnostic_insights/classroom/${classroom.id}/history_and_recommendations/by_student/${selectedStudentId}`, { replace: true });
  }, [selectedStudentId, location.pathname, navigate])

  React.useEffect(() => {
    if (!classroom || !studentHistories || !selectedStudentId) { return }

    if (!studentHistories.find(s => s.user_id === selectedStudentId)) {
      setSelectedStudentId(null)
    }
  }, [classroom, studentHistories])

  React.useEffect(() => {
    if (!classroom) { return }

    if (classroom.id === NO_CLASSROOM.id) {
      setStudentHistories([])
      setSequencePractice({})
    } else {
      getStudentHistories()
      getSequencePractice()
    }
  }, [classroom, useELLDiagnostics, selectedTimeframe])

  React.useEffect(() => {
    window.localStorage.setItem(DIAGNOSTIC_INSIGHTS_USE_ELL, useELLDiagnostics.toString())
  }, [useELLDiagnostics])

  React.useEffect(() => {
    window.localStorage.setItem(DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE, selectedTimeframe.value.toString())
  }, [selectedTimeframe])

  function getPreviouslyAssignedDiagnosticDataForClassroom() {
    if (classroom.id === NO_CLASSROOM.id) { return }

    setPreviouslyAssignedDiagnosticsByLevel(null)
    requestGet(`/diagnostic_insights/diagnostics_assigned/${classroom.id}`, (data) => {
      setPreviouslyAssignedDiagnosticsByLevel(data)
    })
  }

  function getStudentHistories() {
    const requestedClassroomId = classroom.id;
    setStudentHistories(null);

    const queryHash = { timeframe: selectedTimeframe.value };
    if (useELLDiagnostics) {
      queryHash.ell = true;
    }

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: `/diagnostic_insights/history_by_classroom/${requestedClassroomId}`,
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body) => {
      // guards against overwriting the current classroom's data with a request that was fired earlier
      if (classroomIdRef.current === requestedClassroomId) {
        setStudentHistories(body);
      }
    });
  }

  function getSequencePractice() {
    const requestedClassroomId = classroom.id;
    setSequencePractice({});

    const queryHash = { timeframe: selectedTimeframe.value };

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: `/diagnostic_insights/practice_by_classroom/${requestedClassroomId}`,
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body) => {
      // guards against overwriting the current classroom's data with a request that was fired earlier
      if (classroomIdRef.current === requestedClassroomId) {
        setSequencePractice(body);
      }
    });
  }

  function handleTabChange(e) {
    const newTab = e.currentTarget.value;
    const basePath = location.pathname.replace(/\/(history|recommendations)$/, '');
    const newPath = `${basePath}/${newTab === HISTORY ? 'history' : 'recommendations'}`;
    navigate(newPath, { replace: true });
  }

  function handleSetSelectedStudent(selectedStudentOption) {
    setSelectedStudentId(selectedStudentOption.value)
  }

  function selectPreviousStudent() {
    const indexOfCurrentlySelectedStudent = studentHistories.findIndex(studentHistory => studentHistory.user_id === selectedStudentId)
    const newSelectedStudent = studentHistories[indexOfCurrentlySelectedStudent - 1] || studentHistories[studentHistories.length - 1] // go to last if current student was first
    setSelectedStudentId(newSelectedStudent.user_id)
  }

  function selectNextStudent() {
    const indexOfCurrentlySelectedStudent = studentHistories.findIndex(studentHistory => studentHistory.user_id === selectedStudentId)
    const newSelectedStudent = studentHistories[indexOfCurrentlySelectedStudent + 1] || studentHistories[0] // go back to first if current student was last
    setSelectedStudentId(newSelectedStudent.user_id)
  }

  function toggleUseEllDiagnostics() {
    setUseELLDiagnostics(!useELLDiagnostics);
  }

  if (!classroom || (!byClass && !(selectedStudentId && studentHistories))) {
    return (
      <main>
        <Spinner />
      </main>
    );
  }

  const selectedStudentHistory = studentHistories?.find(studentHistory => studentHistory.user_id === selectedStudentId)

  const studentOptions = studentHistories?.map(studentHistory => ({ label: studentHistory.user_name, value: studentHistory.user_id }))

  const studentDropdown = (
    <DropdownInput
      className="small bordered"
      disabled={!studentHistories?.length}
      handleChange={handleSetSelectedStudent}
      id="student-dropdown"
      isSearchable={true}
      label='Student'
      options={studentOptions}
      placeholder='No Students'
      value={studentOptions?.find(studentOption => studentOption.value === selectedStudentId)}
    />
  )

  const timeframeDropdown = (
    <DropdownInput
      ariaLabel={byClass ? 'Timeframe' : null}
      className="small bordered"
      handleChange={setSelectedTimeframe}
      id="timeframe-dropdown"
      isSearchable={true}
      label={byClass ? null : 'Timeframe'}
      options={timeframeOptions}
      value={selectedTimeframe}
    />
  )

  const ellControl = (
    <Checkbox
      label="Switch to ELL Diagnostics"
      onClick={toggleUseEllDiagnostics}
      selected={useELLDiagnostics}
    />
  )

  const renderSubheader = () => {
    if (byClass) {
      // Check if the current route ends with `/history` or `/recommendations`
      const isHistoryTab = /\/history$/.test(location.pathname);
      const activeTab = isHistoryTab ? HISTORY : RECOMMENDATIONS;
      return (
        <div className="subheader">
          <SegmentedControl
            activeTab={activeTab}
            buttons={[
              {
                label: HISTORY,
                onClick: handleTabChange,
              },
              {
                label: RECOMMENDATIONS,
                onClick: handleTabChange,
              },
            ]}
            size="small"
          />
          <div className="ell-and-timeframe-control">
            {ellControl}
            {timeframeDropdown}
          </div>
        </div>
      )
    }

    const fewerThanTwoStudents = studentHistories.length < 2
    const nextAndPreviousButtonsClassName = `quill-button small outlined grey icon ${fewerThanTwoStudents ? 'disabled' : ''}`

    return (
      <div className="subheader student-subheader">
        <div className="student-and-timeframe-control">
          {studentDropdown}
          {timeframeDropdown}
        </div>
        <div className="ell-control-and-next-and-previous-student-buttons">
          {ellControl}
          <button className={nextAndPreviousButtonsClassName} disabled={fewerThanTwoStudents} onClick={selectPreviousStudent} type="button"><img alt="" src={chevronLeftImgSrc} />Previous Student</button>
          <button className={nextAndPreviousButtonsClassName} disabled={fewerThanTwoStudents} onClick={selectNextStudent} type="button">Next Student<img alt="" src={chevronRightImgSrc} /></button>
        </div>
      </div>
    )
  }

  const noStudentHistoriesWithPreDiagnostic = studentHistories && !studentHistories.find(studentHistory => studentHistoryHasPreDiagnostic(studentHistory))

  const routes = (
    <Routes>
      <Route
        element={
          <History
            sequencePractice={sequencePractice}
            showEmptyState={noStudentHistoriesWithPreDiagnostic}
            studentHistories={studentHistories}
            teacherClassroomIds={teacherClassroomIds}
            useELLDiagnostics={useELLDiagnostics}
          />
        }
        path="by_class/history"
      />
      <Route
        element={
          <Recommendations
            classroom={classroom}
            getPreviouslyAssignedDiagnosticDataForClassroom={getPreviouslyAssignedDiagnosticDataForClassroom}
            previouslyAssignedDiagnosticsByLevel={previouslyAssignedDiagnosticsByLevel}
            showEmptyState={noStudentHistoriesWithPreDiagnostic}
            studentHistories={studentHistories}
            useELLDiagnostics={useELLDiagnostics}
          />
        }
        path="by_class/recommendations"
      />
      <Route
        element={
          <IndividualStudent
            classroom={classroom}
            getPreviouslyAssignedDiagnosticDataForClassroom={getPreviouslyAssignedDiagnosticDataForClassroom}
            previouslyAssignedDiagnosticsByLevel={previouslyAssignedDiagnosticsByLevel}
            sequencePractice={sequencePractice}
            showEmptyState={noStudentHistoriesWithPreDiagnostic}
            studentHistory={selectedStudentHistory}
            teacherClassroomIds={teacherClassroomIds}
            timeframe={selectedTimeframe.label}
            useELLDiagnostics={useELLDiagnostics}
          />
        }
        path="by_student/:studentId"
      />
    </Routes>
  )

  return (
    <main>
      <header>
        <h1>
          <b>Diagnostic Insights</b>
          {forwardSlash}
          History and Recommendations
          {forwardSlash}
          {byClass ? 'By Class' : 'By Student'}
          {byClass ? null : <React.Fragment>{forwardSlash}{selectedStudentHistory?.user_name}</React.Fragment>}
        </h1>
        <a className="quill-button outlined small grey" href='https://support.quill.org/en/articles/10751323-how-do-teachers-use-diagnostic-insights' rel="noopener noreferrer" target="_blank">
          <img alt="" src={fileDocumentImgSrc} />
          Guide
        </a>
      </header>
      <MobileSidebarButtonContainer openMobileSidebar={openMobileSidebar} />
      {renderSubheader()}
      {routes}
    </main>
  );
};

export default HistoryAndRecommendations;
